import { useState } from 'react'

import { useLocalization } from '@/shared/i18n'
import { Chip, SearchSheet, Stack } from '@/shared/ui'

import type { AnswerProps } from './types'

// SHEET_FROM is where a row of chips stops being readable. Past it the options
// go behind a searchable sheet — the same one onboarding opens for countries —
// so the question screen keeps its height whatever the catalogue holds.
const SHEET_FROM = 8

function listOf(value: AnswerProps['value']): string[] {
  if (Array.isArray(value)) {
    return value
  }

  return typeof value === 'string' && value !== '' ? [value] : []
}

// Choice answers single_choice and country with one pick, multi_choice and
// countries with several. They are one component because they are one
// interaction: the server has already turned a reference into plain options,
// so a country is a choice like any other by the time it arrives here.
export function Choice({
  question,
  value,
  onChange,
  suggested = [],
  multiple = false,
}: AnswerProps & { multiple?: boolean }) {
  const { t } = useLocalization()
  const [open, setOpen] = useState(false)

  const options = question.options ?? []
  const chosen = listOf(value)
  const viaSheet = question.search || options.length > SHEET_FROM
  const offered = new Set(options.map((option) => option.value))
  const popular = suggested.filter((option) => offered.has(option))
  const extra = chosen.filter((option) => !popular.includes(option))
  const shown = [...popular, ...extra]

  const nameOf = (option: string) =>
    options.find((candidate) => candidate.value === option)?.label ?? option

  function pick(picked: string) {
    if (!multiple) {
      onChange(chosen[0] === picked ? undefined : picked)
      setOpen(false)

      return
    }

    const without = chosen.filter((option) => option !== picked)
    const next = without.length === chosen.length ? [...chosen, picked] : without

    onChange(next.length > 0 ? next : undefined)
  }

  return (
    <>
      <Stack direction="row" gap="sm" wrap className="ask__options">
        {viaSheet ? (
          <>
            {shown.map((option) => (
              <Chip key={option} selected={chosen.includes(option)} onClick={() => pick(option)}>
                {nameOf(option)}
              </Chip>
            ))}
            <Chip onClick={() => setOpen(true)}>
              {shown.length > 0 ? t('survey.choose.other') : t('survey.choose')}
            </Chip>
          </>
        ) : (
          options.map((option) => (
            <Chip
              key={option.value}
              selected={chosen.includes(option.value)}
              onClick={() => pick(option.value)}
            >
              {option.label}
            </Chip>
          ))
        )}
      </Stack>

      {open ? (
        <SearchSheet
          title={question.text}
          placeholder={t('survey.choose.placeholder')}
          items={options.map((option) => ({ value: option.value, name: option.label }))}
          chosen={chosen}
          onPick={pick}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}
